/**
 * Dagen's rules (owner mockup 2026-09-22, docs/handbook/_parked/dagen.md): what each person on
 * today's plan is doing now, which shifts need a manager, how many are on per hour, what the day
 * costs, who is not set up, and the day's clock events in words. Pure, so the page only draws.
 *
 * A deviation here is today's operational one, judged at `now` against the punch tolerance: a
 * clock-in later than the plan allows, no clock-in once the start plus tolerance has passed, or a
 * shift that has ended with nobody clocked out. Timeliste's kinds (timeliste/close.ts) judge a
 * finished shift for month close; these judge a day that is still running.
 */
import type { ApiAbsence, ApiAvailability, ApiShift, ApiUser, ApiWorkTimeChange } from '@/api/data';
import { T } from '@/i18n';
import { oslo, osloISO, plannedHours, plannedPaidHours } from '@/rules';
import { type Paid, type Rates, costOf, hourlyRateOf } from '@/rules/labour';
import type { OpeningHours } from '@/shell/tenant';
import { fullName } from '@/ui/format';
import type { BreakPolicy } from '@timefly/core';
import { addDays, dow, inRange } from '@timefly/core';
import { availOn } from '../vaktplan/model';

const at = (iso: string | null | undefined) => (iso ? Date.parse(iso) : Number.NaN);
const MIN = 60_000;
const HOUR = 3_600_000;

/** "41 min" under an hour, "4 t 0 min" from an hour up: the mockup's two forms. */
export const dur = (m: number) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} t ${m % 60} min`);
/** "14:41", Oslo. */
export const clock = (iso: string | null | undefined) => oslo(iso)?.time ?? '';

export type Status = 'on' | 'break' | 'done' | 'noshow' | 'later';
export const STATUS_TEXT: Record<Status, string> = {
  on: 'På jobb',
  break: 'Pause',
  done: 'Ferdig',
  noshow: 'Ikke møtt',
  later: 'Senere',
};

/**
 * One shift at `now`. Clocked in and out is done; clocked in with a break open is a break;
 * clocked in otherwise is at work, a forgotten clock-out included, which Krever handling names.
 * Not clocked in is later until the start plus tolerance has passed, then it is a no-show.
 */
export function statusOf(s: ApiShift, now: number, tol = 15): Status {
  if (s.punch_in_time) {
    if (s.punch_out_time) return 'done';
    return s.lunch_in_time && !s.lunch_out_time ? 'break' : 'on';
  }
  return now > at(s.start_time) + tol * MIN ? 'noshow' : 'later';
}

export type DevKind = 'open' | 'late' | 'missing';
export interface Deviation {
  shift: ApiShift;
  kind: DevKind;
  /** minutes late for `late`, minutes since the start for `missing`, since the end for `open` */
  minutes: number;
}

/** The one thing wrong with a shift today, worst first: an open punch blocks pay, so it leads. */
export function deviationOf(s: ApiShift, now: number, tol = 15): Deviation | null {
  if (s.user_id == null || s.is_approved) return null;
  const start = at(s.start_time);
  const end = at(s.end_time);
  if (s.punch_in_time && !s.punch_out_time && end < now)
    return { shift: s, kind: 'open', minutes: Math.round((now - end) / MIN) };
  if (s.punch_in_time) {
    const late = at(s.punch_in_time) - start;
    return late > tol * MIN ? { shift: s, kind: 'late', minutes: Math.round(late / MIN) } : null;
  }
  return now > start + tol * MIN
    ? { shift: s, kind: 'missing', minutes: Math.round((now - start) / MIN) }
    : null;
}

export const deviations = (shifts: ApiShift[], now: number, tol = 15): Deviation[] =>
  shifts
    .map((s) => deviationOf(s, now, tol))
    .filter((d): d is Deviation => d != null)
    .sort((a, b) => a.shift.start_time.localeCompare(b.shift.start_time));

/** The red words on a Krever handling row: "41 min sen inn". */
export function deviationLabel(d: Deviation): string {
  if (d.kind === 'late') return `${dur(d.minutes)} sen inn`;
  if (d.kind === 'missing') return 'Ikke stemplet inn';
  return 'Ikke stemplet ut';
}
/** The grey line under it: "Planlagt 14:00, stemplet inn 14:41". */
export function deviationNote(d: Deviation): string {
  const s = d.shift;
  if (d.kind === 'late') return `Planlagt ${clock(s.start_time)}, stemplet inn ${clock(s.punch_in_time)}`;
  if (d.kind === 'missing') return `Planlagt ${clock(s.start_time)}, ${dur(d.minutes)} siden`;
  return `Planlagt slutt ${clock(s.end_time)}, stemplet inn ${clock(s.punch_in_time)}`;
}

/** Status order in the table: what needs a look first, what is over last. */
const ORDER: Status[] = ['noshow', 'on', 'break', 'later', 'done'];
export const byStatus = (rows: { status: Status; shift: ApiShift }[]) =>
  [...rows].sort(
    (a, b) =>
      ORDER.indexOf(a.status) - ORDER.indexOf(b.status) ||
      a.shift.start_time.localeCompare(b.shift.start_time),
  );

export interface HourCount {
  /** the wall-clock hour, 0 to 23; twice 2 on the autumn morning, no 2 on the spring one */
  hour: number;
  count: number;
}
/**
 * People scheduled in each hour of `date`, counted by instant rather than by wall clock, so a
 * shift from yesterday evening counts in this day's early hours, a shift into tomorrow counts up
 * to midnight, and a DST day has 23 or 25 hours. Planned times: this is the plan's cover.
 */
export function perHour(shifts: ApiShift[], date: string): HourCount[] {
  const from = Date.parse(osloISO(date, '00:00'));
  const to = Date.parse(osloISO(addDays(date, 1), '00:00'));
  const out: HourCount[] = [];
  for (let t = from; t < to; t += HOUR) {
    const count = shifts.filter(
      (s) => s.user_id != null && at(s.start_time) < t + HOUR && at(s.end_time) > t,
    ).length;
    out.push({ hour: Number(clock(new Date(t).toISOString()).slice(0, 2)), count });
  }
  return out;
}

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
/**
 * The hours the chart shows: the opening hours and every shift starting that day, whichever
 * reaches further; 08 to 22 when neither says anything. A shift into tomorrow runs to 24.
 */
export function span(shifts: ApiShift[], date: string, opening?: OpeningHours | null): [number, number] {
  let from = Number.POSITIVE_INFINITY;
  let to = Number.NEGATIVE_INFINITY;
  const open = opening?.[DAY_KEYS[dow(date)] ?? 'mon'];
  if (open) {
    const [a, b] = open.map((x) => Number(x.slice(0, 2)) + Number(x.slice(3, 5)) / 60);
    from = Math.floor(a ?? 8);
    to = Math.ceil(b || 24);
  }
  for (const s of shifts) {
    const st = oslo(s.start_time);
    const en = oslo(s.end_time);
    if (s.user_id == null || st?.date !== date || !en) continue;
    from = Math.min(from, Number(st.time.slice(0, 2)));
    const end =
      en.date > date ? 24 : Math.ceil(Number(en.time.slice(0, 2)) + Number(en.time.slice(3, 5)) / 60);
    to = Math.max(to, end);
  }
  return Number.isFinite(from) && Number.isFinite(to) && to > from ? [from, to] : [8, 22];
}

export interface Estimate {
  hours: number;
  people: number;
  /** wage, pension and employer's contribution, rules/labour.ts */
  cost: number;
}
export function estimate(shifts: ApiShift[], rates: Rates, policy: BreakPolicy | null = null): Estimate {
  const mine = shifts.filter((s) => s.user_id != null);
  let h = 0;
  let cost = 0;
  for (const s of mine) {
    // the plan alone, gross, whatever was clocked (#263): a punch never moves a figure labelled planned
    h += plannedHours(s);
    // priced on the planned paid hours: the company's unpaid break comes off, as in pay (2026-10-28)
    cost += costOf(plannedPaidHours(s, policy) * hourlyRateOf(s.user as Paid | null), rates).total;
  }
  return { hours: h, people: new Set(mine.map((s) => s.user_id)).size, cost };
}

/**
 * Active people with no shift on `date`, not away on an approved absence and not marked
 * unavailable, by name. Partly available people stay: they can take part of a day.
 */
export function unscheduled(
  users: ApiUser[],
  shifts: ApiShift[],
  absences: ApiAbsence[],
  avail: ApiAvailability[],
  date: string,
): ApiUser[] {
  const on = new Set(shifts.map((s) => s.user_id));
  const away = (id: number) =>
    absences.some(
      (a) =>
        a.user_id === id &&
        a.status === 'accepted' &&
        inRange(date, oslo(a.start_date)?.date ?? a.start_date, oslo(a.end_date)?.date ?? a.end_date),
    );
  return users
    .filter(
      (u) =>
        u.active_status && !on.has(u.id) && !away(u.id) && availOn(avail, u.id, date)?.kind !== 'unavail',
    )
    .sort((a, b) => fullName(a).localeCompare(fullName(b), 'nb'));
}

const FIELD = {
  punch_in: 'punch_in_time',
  punch_out: 'punch_out_time',
  lunch_in: 'lunch_in_time',
  lunch_out: 'lunch_out_time',
} as const;
const VERB: Record<keyof typeof FIELD, string> = {
  punch_in: '{who} stemplet inn',
  punch_out: '{who} stemplet ut',
  lunch_in: '{who} startet pause',
  lunch_out: '{who} avsluttet pause',
};
export interface Event {
  id: number;
  action: keyof typeof FIELD;
  /** the instant the person clocked, which is the new value of the field, not when it was saved */
  at: string;
  who: string;
  text: string;
  note: string | null;
}

/**
 * The day's clock events from the work-time history, newest first. A clock-in says how late it
 * was against the plan; a clock-out says how long was worked, less a registered break.
 */
export function events(changes: ApiWorkTimeChange[], shifts: ApiShift[], users: ApiUser[]): Event[] {
  const shiftOf = new Map(shifts.map((s) => [s.id, s]));
  const userOf = new Map(users.map((u) => [u.id, u]));
  return changes
    .filter((c): c is ApiWorkTimeChange & { action: keyof typeof FIELD } => c.action in FIELD)
    .map((c) => {
      const v = c.changes?.[FIELD[c.action]]?.[1];
      const when = typeof v === 'string' ? v : c.created_at;
      const s = c.shift_id != null ? shiftOf.get(c.shift_id) : undefined;
      const u = c.user_id != null ? userOf.get(c.user_id) : undefined;
      const who = fullName(u ?? s?.user) || T('Ukjent');
      let note: string | null = null;
      if (c.action === 'punch_in' && s) {
        const late = Math.round((at(when) - at(s.start_time)) / MIN);
        if (late > 0) note = T('{time} senere enn planlagt', { time: dur(late) });
      }
      if (c.action === 'punch_out' && s?.punch_in_time) {
        const pause =
          s.lunch_in_time && s.lunch_out_time ? Math.max(0, at(s.lunch_out_time) - at(s.lunch_in_time)) : 0;
        const m = Math.round((at(when) - at(s.punch_in_time) - pause) / MIN);
        if (m >= 0) note = T('Jobbet {h} t {m} min', { h: Math.floor(m / 60), m: m % 60 });
      }
      return { id: c.id, action: c.action, at: when, who, text: T(VERB[c.action], { who }), note };
    })
    .sort((a, b) => at(b.at) - at(a.at) || b.id - a.id);
}

/** The table as a CSV, semicolons and a BOM for Excel, the way Timeliste exports. */
export function csvOf(rows: string[][]): string {
  return `﻿${rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(';')).join('\n')}`;
}
